import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Report } from '../../types';
import { processAIGeneratedHTML } from '../../utils/htmlPreprocessor';

interface ReportPreviewProps {
  report: Report;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportPreview: React.FC<ReportPreviewProps> = ({ report }) => {
  const [tabValue, setTabValue] = useState(0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'approved':
        return 'success';
      case 'distributed':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const monthName = report?.year && report?.month 
    ? new Date(report.year, report.month - 1).toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      })
    : 'Unknown Period';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatJsonForDisplay = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Monthly Report - {monthName}
        </Typography>
        <Chip 
          label={report?.status?.toUpperCase() || 'UNKNOWN'} 
          color={getStatusColor(report?.status || 'unknown') as any}
        />
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Generated: {report?.generatedAt ? formatDate(report.generatedAt) : 'Unknown'}
        </Typography>
        {report?.approvedAt && (
          <Typography variant="body2" color="text.secondary">
            Approved: {formatDate(report.approvedAt)}
          </Typography>
        )}
        {report?.approvedBy && (
          <Typography variant="body2" color="text.secondary">
            Approved by: {report.approvedBy}
          </Typography>
        )}
        {report?.metadata?.generationMethod && (
          <Typography variant="body2" color="text.secondary">
            Generation Method: {report.metadata.generationMethod === 'bedrock' ? '🤖 AI Generated (Bedrock)' : '📝 Fallback Template'}
          </Typography>
        )}
        {report?.metadata?.modelUsed && (
          <Typography variant="body2" color="text.secondary">
            AI Model: {report.metadata.modelUsed}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="report tabs">
          <Tab label="Report Content" id="report-tab-0" aria-controls="report-tabpanel-0" />
          {report.metadata?.bedrockRequest && (
            <Tab label="Report Generation Info" id="report-tab-1" aria-controls="report-tabpanel-1" />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box 
          sx={{ 
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            minHeight: 400,
            maxHeight: 600,
            overflow: 'auto',
            p: 0, // Remove padding to let HTML content control its own spacing
            
            // Additional containment to prevent layout leakage
            contain: 'layout style size',
            isolation: 'isolate'
          }}
        >
          {/* HTML Report Content */}
          <Box
            data-testid="report-content"
            sx={{
              // CSS containment to prevent layout leakage
              contain: 'layout style',
              isolation: 'isolate',
              
              // Ensure the content stays within bounds
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              overflowWrap: 'break-word',
              
              // Styles for preprocessed AI content
              '& .ai-report-content': {
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                
                // Styles for transformed body elements
                '& .ai-report-content-body': {
                  margin: '0',
                  padding: '20px',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 1.6,
                  color: 'inherit'
                },
                
                // Styles for transformed html elements  
                '& .ai-report-content-html': {
                  margin: '0',
                  padding: '0',
                  fontFamily: 'inherit',
                  fontSize: 'inherit'
                },
                
                // Enhanced heading styles
                '& h1': {
                  color: '#0066cc',
                  borderBottom: '2px solid #0066cc',
                  paddingBottom: '10px',
                  marginBottom: '20px',
                  fontSize: '1.8rem',
                  fontWeight: 600
                },
                '& h2': {
                  color: '#0066cc',
                  marginTop: '20px',
                  marginBottom: '10px',
                  fontSize: '1.4rem',
                  fontWeight: 500
                },
                '& h3': {
                  color: '#0066cc',
                  marginTop: '15px',
                  marginBottom: '8px',
                  fontSize: '1.2rem',
                  fontWeight: 500
                },
                '& p': {
                  lineHeight: 1.6,
                  marginBottom: '10px',
                  color: 'inherit'
                },
                
                // Ensure all content stays within bounds
                '& *': {
                  maxWidth: '100% !important',
                  boxSizing: 'border-box !important'
                }
              }
            }}
            dangerouslySetInnerHTML={{ 
              __html: processAIGeneratedHTML(report?.content || '<p>No content available</p>')
            }}
          />
        </Box>
      </TabPanel>

      {report.metadata?.bedrockRequest && (
        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This tab shows technical details about how the report was generated, including the AI model configuration, 
            input data summary, and the prompt used for report generation.
          </Alert>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Bedrock Configuration</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" gutterBottom><strong>Model:</strong> {report.metadata.bedrockRequest.modelId}</Typography>
              <Typography variant="body2" gutterBottom><strong>Max Tokens:</strong> {report.metadata.bedrockRequest.maxTokens}</Typography>
              <Typography variant="body2" gutterBottom><strong>Temperature:</strong> {report.metadata.bedrockRequest.temperature}</Typography>
              {report.metadata.bedrockRequest.topP && (
                <Typography variant="body2" gutterBottom><strong>Top P:</strong> {report.metadata.bedrockRequest.topP}</Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Input Data Summary</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" gutterBottom>
                <strong>Submissions:</strong> {report.metadata.bedrockRequest.inputData.submissions.length} items
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Writing Tenets:</strong> {report.metadata.bedrockRequest.inputData.writingTenets.length} items
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Stakeholder Emails:</strong> {report.metadata.bedrockRequest.inputData.stakeholderEmails.length} items
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Generated Prompt</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box 
                sx={{ 
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: 'auto'
                }}
              >
                <Typography 
                  variant="body2" 
                  component="pre"
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                >
                  {report.metadata.bedrockRequest.prompt}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Full Request Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box 
                sx={{ 
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: 'auto'
                }}
              >
                <Typography 
                  variant="body2" 
                  component="pre"
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem'
                  }}
                >
                  {formatJsonForDisplay(report.metadata.bedrockRequest)}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </TabPanel>
      )}

      <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          Report ID: {report?.id || 'Unknown'}
        </Typography>
        {report?.metadata && (
          <>
            {' • '}
            <Typography variant="caption" color="text.secondary">
              Submissions: {report.metadata.submissionCount || 0}
            </Typography>
            {report.metadata.processingTimeMs && (
              <>
                {' • '}
                <Typography variant="caption" color="text.secondary">
                  Processing: {report.metadata.processingTimeMs}ms
                </Typography>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ReportPreview;
